import React, { useEffect, useState } from 'react';
import { loadYaml } from '../utilis/loadYAML';
import emailjs from 'emailjs-com';
import {
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaTimes,
} from 'react-icons/fa';
import './contact.css';

// Use loose typing for icons to avoid TS errors (same as your force.tsx)
const iconMap: Record<string, any> = {
  map: FaMapMarkerAlt,
  phone: FaPhone,
  email: FaEnvelope,
  close: FaTimes,
};

const getIcon = (name: string): React.ReactNode | null => {
  const IconComponent = iconMap[name];
  if (!IconComponent) {
    console.warn(`Icon not found for: ${name}`);
    return null;
  }
  return React.createElement(IconComponent, { className: 'icon' });
};

interface ContactOption {
  icon: string;
  title: string;
  description: string;
}

interface Section {
  type: string;
  id?: string;
  title?: string;
  description?: string;
  image?: string;
  contactOptions?: ContactOption[];
}

const ContactForm: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    emailjs.send(
      'YOUR_SERVICE_ID',
      'YOUR_TEMPLATE_ID',
      formData,
      'YOUR_USER_ID'
    ).then(() => {
      setSubmitted(true);
      setFormData({ name: '', email: '', message: '' });
    }).catch(console.error);
  };

  return (
    <>
      <div className="contact-overlay-backdrop" onClick={onClose}></div>
      <div className="contact-modal" role="dialog" aria-modal="true">
        <form className="contact-form" onSubmit={handleSubmit}>
          <button
            className="close-btn"
            type="button"
            onClick={onClose}
            aria-label="Close contact form"
          >
            {getIcon('close')}
          </button>

          <label>
            Name
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              autoComplete="name"
            />
          </label>

          <label>
            Email
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              autoComplete="email"
            />
          </label>

          <label>
            Message
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
            />
          </label>

          <button type="submit">Send</button>
          {submitted && <p className="submitted-msg">Thanks! Your message has been sent.</p>}
        </form>
      </div>
    </>
  );
};

const Contact: React.FC = () => {
  const [sections, setSections] = useState<Section[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    loadYaml('/config/contact.yaml')
      .then((data) => {
        if (data && data.sections) setSections(data.sections);
        else setSections([]);
      })
      .catch((error) => {
        console.error('Error loading YAML:', error);
        setSections([]);
      });
  }, []);

  return (
    <div className="contactpage-container">
      {sections.map((section, idx) => {
        const renderHr = idx !== 0 && section.type !== 'hero';

        switch (section.type) {
          case 'hero':
            return (
              <section
                key={idx}
                id={section.id}
                className="contact-hero"
                style={{ backgroundImage: `url(${section.image})` }}
              >
                {section.title && (
                  <div className="overlay">
                    <h1 className="hero-title">{section.title}</h1>
                  </div>
                )}
              </section>
            );

          case 'text': {
            const [boldLine, ...rest] = (section.description || '').split('---');
            return (
              <React.Fragment key={idx}>
                {renderHr && <hr className="section-divider" />}
                <section id={section.id} className="contact-text-section">
                  {section.title && <h2>{section.title}</h2>}
                  <div className="text-bifurcated-container">
                    <div className="text-bifurcated-left">
                      {boldLine && <p className="bold-line">{boldLine.trim()}</p>}
                    </div>
                    <div className="text-bifurcated-right">
                      {rest.length > 0 && (
                        <p className="secondary-text">{rest.join('---').trim()}</p>
                      )}
                    </div>
                  </div>
                </section>
              </React.Fragment>
            );
          }

          case 'info':
            return (
              <React.Fragment key={idx}>
                {renderHr && <hr className="section-divider" />}
                <section id={section.id} className="contact-info">
                  {section.title && <h2>{section.title}</h2>}
                  <div className="contact-grid">
                    {section.contactOptions?.map((opt, i) => (
                      <div key={i} className="contact-box">
                        <div className="icon">{getIcon(opt.icon)}</div>
                        <h3>{opt.title}</h3>
                        <p>{opt.description}</p>
                      </div>
                    ))}
                  </div>
                </section>
              </React.Fragment>
            );

          case 'form':
            return (
              <React.Fragment key={idx}>
                {renderHr && <hr className="section-divider" />}
                <section id={section.id} className="contact-form-section">
                  <button
                    className="contact-toggle-btn"
                    onClick={() => setIsModalOpen(true)}
                  >
                    Contact Us
                  </button>
                </section>
              </React.Fragment>
            );

          default:
            return null;
        }
      })}

      {isModalOpen && <ContactForm onClose={() => setIsModalOpen(false)} />}
    </div>
  );
};

export default Contact;
